import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from "@reduxjs/toolkit/query"
import { fetchBaseQuery } from "@reduxjs/toolkit/query"
import { v4 as uuidv4 } from "uuid"
import { getAuthStrategy } from "../utils"

declare global {
  interface Window {
    Auth0?: {
      getAccessTokenSilently: () => Promise<string>
      // Standalone only (Auth0AuthProvider). Absent in the dev/agentic shim.
      logout?: () => Promise<void> | void
    }
  }
}

// Read environment variables
const VITE_SL2_API_V2_URL = import.meta.env.VITE_SL2_API_V2_URL
// For parent/embedded strategy: only the path is configured; the origin is derived from the parent window
const VITE_SL2_API_EMBEDDED_PATH = import.meta.env.VITE_SL2_API_EMBEDDED_PATH

// Determine strategy and base URL at module load time
const strategy = getAuthStrategy()

function resolveApiUrl(): string {
  if (strategy === "auth0") return VITE_SL2_API_V2_URL
  // parent strategy: derive origin from parent, append configured path
  const parentOrigin = window.parent !== window
    ? new URL(document.referrer).origin
    : window.location.origin
  return parentOrigin + (VITE_SL2_API_EMBEDDED_PATH || "/sl2-api")
}

/**
 * The resolved API origin+path every SL2 request is built on.
 *
 * Exported (LOCKER-355) so the SA locker console can PRINT the exact URL it
 * read a payload from. When the page and a manual `curl` disagree, the only
 * thing that settles it is which endpoint each one actually hit — and that was
 * knowable from the code alone. Read-only; nothing outside this module builds a
 * request from it.
 */
export const sl2ApiUrl = resolveApiUrl()

// Only validate the URL required for the active strategy
if (!sl2ApiUrl) {
  throw new Error(
    `Missing API URL for auth strategy "${strategy}". ` +
      (strategy === "parent"
        ? "Set VITE_SL2_API_EMBEDDED_PATH"
        : "Set VITE_SL2_API_V2_URL"),
  )
}

// Thrown by prepareAuthHeaders when no token can be obtained for a strategy
// that requires one. Caught in the outer baseQuery to short-circuit the request.
class MissingAuthTokenError extends Error {
  constructor(public readonly reason: string) {
    super(reason)
    this.name = "MissingAuthTokenError"
  }
}

async function prepareAuthHeaders(headers: Headers) {
  headers.set("idempotency-key", uuidv4())
  headers.set("ts", Date.now().toString())
  headers.set("Mbls-Sl2-Wfe-Auth-Strategy", strategy)

  if (strategy === "auth0") {
    let token: string | null | undefined
    try {
      token = await window.Auth0?.getAccessTokenSilently()
    } catch (error) {
      throw new MissingAuthTokenError(`Auth0 getAccessTokenSilently threw: ${(error as Error)?.message ?? error}`)
    }
    if (!token) throw new MissingAuthTokenError("No Auth0 token available")
    headers.set("authorization", `Bearer ${token}`)
  }
  // For parent strategy, no token needed — cookies are sent via credentials: "include"
  return headers
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: sl2ApiUrl,
  // Parent strategy uses cookie-based auth — must include credentials
  ...(strategy === "parent" && {
    credentials: "include" as RequestCredentials,
  }),
  prepareHeaders: async (headers) => {
    return prepareAuthHeaders(headers)
  },
})

// Mutex: prevents thundering-herd when multiple API calls 401 simultaneously
let refreshPromise: Promise<void> | null = null

async function refreshToken(): Promise<void> {
  if (strategy === "auth0") {
    // Auth0 SDK refreshes the token automatically via getAccessTokenSilently()
    await window.Auth0?.getAccessTokenSilently()
  }
}

function syntheticUnauthorized(reason: string): { error: FetchBaseQueryError } {
  console.error("[sl2-baseQuery] short-circuiting request:", reason)
  return {
    error: {
      status: 401,
      data: { code: "MISSING_AUTH_TOKEN", reason },
    } as FetchBaseQueryError,
  }
}

async function runRawBaseQuery(
  args: string | FetchArgs,
  api: Parameters<typeof rawBaseQuery>[1],
  extraOptions: Parameters<typeof rawBaseQuery>[2],
) {
  // fetchBaseQuery does not consistently catch throws from prepareHeaders,
  // so we guard the call here and convert MissingAuthTokenError into a 401.
  try {
    return await rawBaseQuery(args, api, extraOptions)
  } catch (error) {
    if (error instanceof MissingAuthTokenError) {
      return syntheticUnauthorized(error.reason)
    }
    throw error
  }
}

/**
 * Does this response mean the backend no longer accepts the session?
 *
 * Two shapes reach the browser for a dead / expired / revoked token:
 * - 401: a handler `AuthenticationError`, or our synthetic MISSING_AUTH_TOKEN.
 * - 403 from API Gateway: IdpMainAuthorizer never throws, it returns a Deny
 *   policy (apps/idp/src/fnMainAuthorizer.ts), which the gateway renders as
 *   ACCESS_DENIED with body `{"Message":"User is not authorized to access this
 *   resource with an explicit deny"}`. Capital-M `Message` is the gateway shape.
 *
 * A handler 403 (`errorType: "AuthorizationError"`, e.g. missing IAM action or
 * wrong tenant) is a permission denial, not a dead session — never log out on it.
 */
const GATEWAY_DENY_MESSAGE = /explicit deny|not authorized to access this resource/i
export const isSessionRejected = (status: unknown, body: unknown): boolean => {
  if (status === 401) return true
  if (status !== 403) return false
  const message = (body as { Message?: unknown } | null | undefined)?.Message
  return typeof message === "string" && GATEWAY_DENY_MESSAGE.test(message)
}

// One logout per page load: concurrent rejected calls must not race N logouts.
// Logout navigates away, so the flag resets with the next page.
let sessionLogoutStarted = false

/**
 * Standalone (auth0) only: the backend rejected the session, so end the
 * frontend session too. Same path as /signout (`auth0.logout()`), which clears
 * the localstorage token cache AND the Auth0 SSO session — the next
 * `loginWithRedirect` shows the login form instead of silently re-issuing a
 * token the backend rejects (no redirect loop).
 *
 * Parent (embedded) mode is untouched: the host webapp owns that session.
 */
function endRejectedSession(reason: string) {
  if (strategy !== "auth0" || sessionLogoutStarted) return
  const logout = window.Auth0?.logout
  if (!logout) return
  sessionLogoutStarted = true
  console.warn("[sl2-baseQuery] backend rejected the session — logging out:", reason)
  localStorage.removeItem("tenantId")
  Promise.resolve(logout()).catch((error) => {
    console.error("[sl2-baseQuery] logout failed", error)
  })
}

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta> = async (args, api, extraOptions) => {
  const result = await runRawBaseQuery(args, api, extraOptions)

  if (!isSessionRejected(result.error?.status, result.error?.data)) {
    return result
  }

  // Rejected — attempt one token refresh (coalesced across concurrent calls)
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null
    })
  }

  try {
    await refreshPromise
  } catch {
    // Refresh failed — return the error as-is.
    return result
  }

  // Retry the original request with the refreshed token
  const retried = await runRawBaseQuery(args, api, extraOptions)
  const retriedData = retried.error?.data as { code?: string } | undefined
  if (
    isSessionRejected(retried.error?.status, retried.error?.data) &&
    // No token could be obtained (e.g. Auth0 network error): the backend never
    // answered, so this is not a backend rejection. login_required is already
    // escalated to loginWithRedirect in Auth0AuthProvider.
    retriedData?.code !== "MISSING_AUTH_TOKEN"
  ) {
    endRejectedSession(`HTTP ${retried.error?.status}`)
  }
  return retried
}

// native fetch version with prepared headers
export const fetchSl2Api = async (input: RequestInfo, init?: RequestInit) => {
  const headers = new Headers(init?.headers)
  await prepareAuthHeaders(headers)

  const fetchInit: RequestInit = {
    ...init,
    headers,
    ...(strategy === "parent" && { credentials: "include" }),
  }

  // Prepend base URL for string inputs
  const url = typeof input === "string" ? sl2ApiUrl + input : input
  const response = await fetch(url, fetchInit)
  if (response.status === 401) {
    endRejectedSession("HTTP 401")
  } else if (response.status === 403 && strategy === "auth0") {
    // Peek at a clone so the caller can still read the body.
    const body = await response.clone().json().catch(() => undefined)
    if (isSessionRejected(403, body)) endRejectedSession("HTTP 403 gateway deny")
  }
  return response
}

/**
 * Turn whatever `unwrap()` rejected with into something a human can read.
 *
 * RTK Query does NOT reject with an `Error`: it rejects with a
 * `FetchBaseQueryError` (`{ status, data }`, or `{ status: "FETCH_ERROR",
 * error }`) or a `SerializedError`. Neither has `.message`, so the obvious
 * `error?.message ?? String(error)` renders the literal string
 * "[object Object]" — which is what the reservation-sync report showed instead
 * of naming a CORS failure, and cost a diagnosis.
 */
export const describeQueryError = (error: unknown): string => {
  if (!error) return "unknown error"
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  const e = error as Record<string, any>

  // Transport-level: the request never produced an HTTP response. A blocked
  // CORS preflight and a dead host both land here, so say so explicitly.
  if (e.status === "FETCH_ERROR")
    return `network or CORS failure — the response never reached the browser${
      e.error ? ` (${e.error})` : ""
    }`
  if (e.status === "PARSING_ERROR")
    return `the server returned a body that is not JSON (HTTP ${e.originalStatus})`
  if (e.status === "TIMEOUT_ERROR") return "the request timed out"
  if (e.status === "CUSTOM_ERROR") return e.error ?? "request failed"

  // HTTP-level: prefer the API's own message over a bare status code.
  const body = e.data
  const fromBody =
    typeof body === "string"
      ? body
      : (body?.message ?? body?.errorMessage ?? body?.error)
  if (typeof e.status === "number")
    return fromBody ? `HTTP ${e.status} — ${fromBody}` : `HTTP ${e.status}`

  return e.message ?? fromBody ?? JSON.stringify(error)
}
